/**
 * The Engine Room: one page for the state of the ship.
 *
 * It is a reading surface, not a second cockpit. Every figure on it comes from
 * the model in lib/engine-room; every control on it hands off to a detail
 * surface that already exists - the machine's capacity controls, the AI Usage
 * drawer, the Connector Center, the Work Board. Nothing here starts, stops,
 * routes or changes anything.
 *
 * The hierarchy is deliberate. The captain's line is read first and is one
 * band across the top. The engine bank is the largest thing below it, because
 * "how hard is each machine working and what can it still take" is the question
 * the owner opens this page with. The Machinist sits in its own column and
 * stays beside the evidence it is talking about. AI, ship systems and work are
 * peers under the bank.
 */
import type { ReactElement } from 'react';
import type { EngineRoomModel, EngineRoomAiLane, EngineRoomState } from '../lib/engine-room';
import type { MachineMeter } from '@brunel/shared';
import type { EngineRoomMachine } from '../lib/engine-room-machines';
import type { MachinistFinding, MachinistReport, MachinistTarget } from '../lib/machinist';
import type { SystemConnectorType, SystemMonitorCard } from '../lib/systems-monitor';
import { AiUsageGauge } from './AiUsageGauge';
import '../engine-room.css';

export interface EngineRoomProps {
  readonly model: EngineRoomModel;
  readonly report: MachinistReport;
  /**
   * Open this computer's own detail. The room wires this to the existing
   * Machine Capacity drawer; the Machine Board (#492) retargets this one
   * callback rather than adding a second way in.
   */
  readonly onOpenMachine: (nodeId: string) => void;
  /** Open this lane in the existing AI Usage detail. */
  readonly onOpenAiLane: (key: string) => void;
  /** Open this system in the existing Connector Center. */
  readonly onOpenSystem: (typeKey: SystemConnectorType) => void;
  /** Open the existing Work Board. */
  readonly onOpenWork: () => void;
  /** Hand the Machinist's report to the one Main Command conversation. */
  readonly onReportToMainCommand: () => void;
}

const STATE_HINTS: Readonly<Record<EngineRoomState, string>> = {
  healthy: 'Every reading that exists is clean.',
  attention: 'Something wants reading, but work can still go on.',
  constrained: 'Work is limited by capacity, not by a fault.',
  failing: 'Something the ship depends on is reporting a failure.',
  unknown: 'Nothing has reported. Brunel is not guessing at a state.',
};

function timeLabel(value: string | null): string {
  if (value === null) return 'Unknown';
  const at = new Date(value);
  if (Number.isNaN(at.getTime())) return 'Unknown';
  return at.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function ratio(used: number | null, total: number | null): string {
  if (total === null) return 'Unknown';
  return `${used ?? 0} / ${total}`;
}

/**
 * One reading, drawn the same width as every other reading on the page.
 *
 * A reading that does not exist gets a struck-through track and the word
 * Unknown - never an empty bar, which reads as zero, and never a filled one.
 * Where the owner has set a limit on that reading, a tick marks it on the
 * track, so how close the machine is to it can be read at a glance.
 */
function Gauge({ label, percent, value, tone = 'neutral', title, limitPercent = null, limitText = null }: {
  readonly label: string;
  /** 0-100 when measured; null when the machine did not report it. */
  readonly percent: number | null;
  readonly value: string;
  readonly tone?: GaugeTone;
  readonly title?: string;
  readonly limitPercent?: number | null;
  readonly limitText?: string | null;
}): ReactElement {
  const known = percent !== null && Number.isFinite(percent);
  const width = known ? Math.max(0, Math.min(100, percent)) : 0;
  const limit = limitPercent !== null && Number.isFinite(limitPercent) && limitPercent > 0 && limitPercent < 100
    ? limitPercent : null;
  return (
    <div className={`engine-gauge tone-${tone}${known ? '' : ' is-unknown'}`} title={title ?? `${label}: ${value}`}>
      <span className="engine-gauge-label">{label}</span>
      <span
        className="engine-gauge-track"
        role="meter"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={known ? width : undefined}
        aria-valuetext={known ? `${Math.round(width)}%` : 'Unknown'}
      >
        {known && <span className="engine-gauge-fill" style={{ width: `${width}%` }} />}
        {limit !== null && (
          <span
            className="engine-gauge-limit"
            style={{ left: `${limit}%` }}
            aria-hidden="true"
            title={limitText === null ? undefined : `Your limit: ${limitText}`}
          />
        )}
      </span>
      <span className="engine-gauge-value">{value}</span>
    </div>
  );
}

type GaugeTone = 'ok' | 'attention' | 'danger' | 'neutral';

function machineTone(machine: EngineRoomMachine): GaugeTone {
  if (machine.connection === 'disconnected') return 'danger';
  if (machine.connection === 'stale') return 'attention';
  if (machine.readiness === 'ready') return 'ok';
  if (machine.readiness === 'not_reported') return 'neutral';
  return 'attention';
}

/**
 * A #483 meter's tone, on this page's scale. The reading that is actually
 * holding the machine back is drawn as danger; one merely past a limit is
 * attention; an unmeasured one is neutral and never looks healthy.
 */
function meterTone(meter: MachineMeter, machine: EngineRoomMachine): GaugeTone {
  if (!meter.known) return 'neutral';
  if (meter.tone !== 'attention') return 'ok';
  return machine.heldBackBy !== null && machine.heldBackBy === meter.key ? 'danger' : 'attention';
}

function heldBackText(machine: EngineRoomMachine): string {
  if (machine.stance === 'held_back') return machine.reason ?? 'Reason not stated';
  if (machine.stance === 'at_seat_limit') return 'Every Worker seat is in use';
  if (machine.stance === 'unknown') return 'Unknown';
  return 'Nothing';
}

function recentText(machine: EngineRoomMachine): string {
  if (machine.recentWindowRuns === null) return 'Unknown';
  const runs = machine.recentWindowRuns === 0 ? 'None' : `${machine.recentWindowRuns} finished`;
  return `${runs} in the last ${machine.recentWindowMinutes} min`;
}

function MachineCard({ machine, onOpen }: {
  readonly machine: EngineRoomMachine;
  readonly onOpen: (nodeId: string) => void;
}): ReactElement {
  const local = machine.localRuntime;
  const connection = machine.connection === 'connected'
    ? 'Connected' : machine.connection === 'stale' ? 'Stale' : 'Offline';
  const guardLines = machine.guards.filter((guard) => guard.ceilingPercent !== null);
  return (
    <article
      className={`engine-machine tone-${machineTone(machine)}`}
      data-node={machine.nodeId}
      data-readiness={machine.readiness}
      data-stance={machine.stance}
      data-profile={machine.profile ?? 'unknown'}
      data-connection={machine.connection}
      aria-label={`${machine.name} machine`}
    >
      <header className="engine-machine-head">
        {/* The one way into this machine's own detail. It opens whatever the
            room wires to onOpenMachine - today the Machine Capacity drawer,
            and the Machine Board (#492) once that exists - never a copy. */}
        <button
          type="button"
          className="engine-machine-name"
          data-open-machine={machine.nodeId}
          title={`Open ${machine.name}: capacity, Workers and limits`}
          onClick={() => onOpen(machine.nodeId)}
        >
          {machine.name}
        </button>
        <span className="engine-chip" data-tone={machineTone(machine)}>{connection}</span>
        <span className="engine-chip engine-chip-quiet">{machine.statusPhrase}</span>
        <span className="engine-chip engine-chip-profile" data-profile={machine.profile ?? 'unknown'}
          title={machine.profileLine ?? 'This machine’s limits are not all stated, so its level is Unknown.'}>
          {machine.profileLabel}
        </span>
      </header>

      <p className="engine-machine-line">{machine.statusLine}</p>

      <div className="engine-gauges">
        {machine.meters.map((meter) => (
          <Gauge
            key={meter.key}
            label={meter.label}
            percent={meter.percent}
            value={meter.text}
            tone={meterTone(meter, machine)}
            limitPercent={meter.key === 'seats' ? null : meter.limitPercent}
            limitText={meter.limitText}
            title={[`${meter.label}: ${meter.text}`, meter.detail, meter.limitText === null ? null : `limit ${meter.limitText}`]
              .filter((part): part is string => part !== null).join(' · ')}
          />
        ))}
      </div>

      <div className="engine-remaining" aria-label={`Room left on ${machine.name}`}>
        <small className="muted">Room before your limits</small>
        <ul>
          {machine.remaining.map((room) => (
            <li
              key={room.key}
              data-remaining={room.key}
              data-known={room.known ? 'yes' : 'no'}
              data-exhausted={room.exhausted ? 'yes' : 'no'}
              title={room.detail}
            >
              <span>{room.label}</span>
              <strong>{room.text}</strong>
            </li>
          ))}
        </ul>
      </div>

      {(guardLines.length > 0 || machine.nearingLine !== null) && (
        <ul className="engine-guards">
          {guardLines.map((guard) => (
            <li key={guard.dimension} data-guard={guard.dimension}
              data-state={guard.engaged ? 'engaged' : guard.unmeasured ? 'unmeasured' : 'clear'}>
              {guard.line}
            </li>
          ))}
          {machine.nearingLine !== null && (
            <li data-guard="nearing" data-state="nearing">Nearing a limit: {machine.nearingLine}.</li>
          )}
        </ul>
      )}

      <dl className="engine-facts">
        <div><dt>Accepting work</dt><dd>{machine.stanceLabel}</dd></div>
        <div><dt>Held back by</dt><dd>{heldBackText(machine)}</dd></div>
        <div><dt>Profile</dt><dd>
          {machine.profileLabel}
          {machine.profileLine !== null && <small className="muted engine-profile-line">{machine.profileLine}</small>}
        </dd></div>
        <div><dt>Node version</dt><dd>
          {machine.runningVersion ?? 'Unknown'}
          {machine.versionBehind === true && <span className="engine-chip engine-chip-warn"> behind {machine.targetVersion ?? 'target'}</span>}
        </dd></div>
        <div><dt>Reading</dt><dd>{machine.readingAge ?? timeLabel(machine.observedAt)}{machine.stale ? ' · stale' : ''}</dd></div>
        <div><dt>Last seen</dt><dd>{machine.lastSeen}</dd></div>
        <div><dt>Recent runs</dt><dd>{recentText(machine)}</dd></div>
      </dl>

      <div className="engine-machine-foot">
        <span className="stack tight">
          <small className="muted">Local models</small>
          <strong>{local === null
            ? 'No local runtime reported'
            : local.loadedModelsKnown && local.loadedModels.length > 0
              ? local.loadedModels.join(' · ')
              : local.loadedModelsKnown ? 'Nothing loaded' : 'Loaded model Unknown'}</strong>
          {local !== null && (
            <small className="muted">
              {`${local.serviceState === 'reachable' ? 'Service reachable' : local.serviceState === 'unreachable' ? 'Service unavailable' : 'Service Unknown'}`
                + ` · ${local.installedModelCount === null ? 'installed Unknown' : `${local.installedModelCount} installed`}`
                + `${local.offeredModels.length > 0 ? ` · offers ${local.offeredModels.join(', ')}` : ''}`}
            </small>
          )}
        </span>
        <span className="stack tight">
          <small className="muted">Running here</small>
          <strong>{machine.runningTitles.length === 0 ? 'Nothing running' : `${machine.runningTitles.length} active`}</strong>
          {machine.runningTitles.slice(0, 2).map((title) => (
            <small className="muted" key={title}>{title}</small>
          ))}
        </span>
        <button type="button" className="link engine-machine-open" onClick={() => onOpen(machine.nodeId)}
          aria-label={`Open ${machine.name}`}>
          Open machine ›
        </button>
      </div>
    </article>
  );
}

const LANE_GROUP_LABELS: Readonly<Record<EngineRoomAiLane['group'], string>> = {
  bridge: 'External bridges',
  cloud: 'Accounts and quotas',
  local: 'Local runtimes',
  runtime: 'Installed runtimes and teammates',
};

function AiSection({ model, onOpenAiLane }: {
  readonly model: EngineRoomModel;
  readonly onOpenAiLane: (key: string) => void;
}): ReactElement {
  const groups: readonly EngineRoomAiLane['group'][] = ['bridge', 'cloud', 'local', 'runtime'];
  return (
    <section className="engine-section" aria-label="AI power and crew capacity" data-engine-section="ai">
      <header className="engine-section-head">
        <h2>AI power</h2>
        <small className="muted">
          {model.ai.lanes.length === 0
            ? 'No AI lane is reporting'
            : `${model.ai.available} available · ${model.ai.limited} limited · ${model.ai.exhausted} exhausted · ${model.ai.unavailable} unavailable · ${model.ai.unknown} Unknown`}
        </small>
      </header>
      {model.ai.lanes.length === 0 ? (
        <p className="muted">No provider, bridge or local runtime has reported. Capacity stays Unknown until one does.</p>
      ) : (
        groups.filter((group) => model.ai.lanes.some((lane) => lane.group === group)).map((group) => (
          <div className="engine-ai-group" key={group}>
            <h3>{LANE_GROUP_LABELS[group]}</h3>
            <ul className="engine-ai-lanes">
              {model.ai.lanes.filter((lane) => lane.group === group).map((lane) => (
                <li key={lane.key} className="engine-ai-lane" data-availability={lane.availability}>
                  <button
                    type="button"
                    className="engine-ai-open"
                    aria-label={`Open ${lane.title} usage detail`}
                    title={`${lane.title} · ${lane.availability}${lane.capability === null ? '' : ` · ${lane.capability}`}`}
                    onClick={() => onOpenAiLane(lane.key)}
                  />
                  <AiUsageGauge meter={lane.meter} size="compact" />
                  {/* The compact gauge clips a long name to stay square, and this
                      page is where the owner asks which runtime is available - so
                      the name is written out again underneath, in full. */}
                  <span className="engine-ai-foot">
                    <span className="engine-chip" data-availability={lane.availability}>{lane.availability}</span>
                    <small>{lane.title}</small>
                    {lane.capability !== null && <small className="muted">{lane.capability}</small>}
                    {lane.nodeName !== null && <small className="muted">{lane.nodeName}</small>}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ))
      )}
    </section>
  );
}

function SystemsSection({ systems, onOpenSystem }: {
  readonly systems: readonly SystemMonitorCard[];
  readonly onOpenSystem: (typeKey: SystemConnectorType) => void;
}): ReactElement {
  return (
    <section className="engine-section" aria-label="Ship systems" data-engine-section="systems">
      <header className="engine-section-head">
        <h2>Ship systems</h2>
        <small className="muted">Supabase, GitHub, Cloudflare and any future service adapter</small>
      </header>
      <div className="engine-systems">
        {systems.map((card) => (
          <button
            type="button"
            key={card.typeKey}
            className="engine-system"
            data-system={card.typeKey}
            data-status={card.status.toLowerCase()}
            title={`${card.label} · ${card.status} · ${card.source}`}
            onClick={() => onOpenSystem(card.typeKey)}
          >
            <span className="row spread">
              <strong>{card.label}</strong>
              <span className="engine-chip" data-status={card.status.toLowerCase()}>{card.status}</span>
            </span>
            <small>{card.summary}</small>
            <dl className="engine-system-facts">
              {card.facts.filter((fact) => fact.value !== 'Unknown').slice(0, 2).map((fact) => (
                <div key={fact.id}><dt>{fact.label}</dt><dd>{fact.value}</dd></div>
              ))}
            </dl>
            <small className="muted">{card.observedAt === null ? 'No timestamped evidence' : `Observed ${timeLabel(card.observedAt)}`}</small>
          </button>
        ))}
      </div>
    </section>
  );
}

function WorkSection({ model, onOpenWork }: {
  readonly model: EngineRoomModel;
  readonly onOpenWork: () => void;
}): ReactElement {
  const work = model.work;
  return (
    <section className="engine-section" aria-label="Work and load" data-engine-section="work">
      <header className="engine-section-head">
        <h2>Work and load</h2>
        <button type="button" className="link" onClick={onOpenWork}>Open the Work Board</button>
      </header>

      <div className="engine-work-counts">
        <span className="engine-count"><strong>{work.running.length}</strong><small>running</small></span>
        <span className="engine-count"><strong>{work.queuedCount}</strong><small>queued</small></span>
        <span className="engine-count" data-tone={work.blockedCount > 0 ? 'attention' : undefined}>
          <strong>{work.blockedCount}</strong><small>blocked</small>
        </span>
        <span className="engine-count"><strong>{work.review.length}</strong><small>in review</small></span>
        <span className="engine-count"><strong>{work.doneRecently}</strong><small>done recently</small></span>
      </div>

      <div className="engine-work-columns">
        <div className="stack tight">
          <h3>Running, by machine</h3>
          {work.running.length === 0 ? (
            <p className="muted">Nothing is running.</p>
          ) : (
            <ul className="engine-list">
              {work.running.map((run) => (
                <li key={run.assignment_id}>
                  <strong>{run.title}</strong>
                  <small className="muted">
                    {run.node_name} · {run.worker_name ?? (run.worker_number === null ? 'Worker Unknown' : `Worker ${run.worker_number}`)}
                  </small>
                </li>
              ))}
            </ul>
          )}
          <ul className="engine-distribution">
            {work.distribution.map((row) => (
              <li key={row.nodeName}><span>{row.nodeName}</span><span>{row.running} running</span></li>
            ))}
          </ul>
        </div>

        <div className="stack tight">
          <h3>Held up</h3>
          {work.blocked.length === 0 ? (
            <p className="muted">Nothing is blocked.</p>
          ) : (
            <ul className="engine-list">
              {work.blocked.slice(0, 6).map((card) => (
                <li key={card.id}>
                  <button type="button" className="link" onClick={onOpenWork}>{card.title}</button>
                  <small className="muted">{card.blockerOrApproval ?? card.currentState}</small>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="stack tight">
          <h3>Where new work could go</h3>
          <ul className="engine-routes">
            {work.routes.length === 0 && <li className="muted">No computer is reporting.</li>}
            {work.routes.map((route) => (
              <li key={route.nodeId} data-eligible={route.eligible ? 'yes' : 'no'}>
                <span className="engine-chip" data-tone={route.eligible ? 'ok' : 'attention'}>
                  {route.eligible ? 'Eligible' : 'Not now'}
                </span>
                <span className="stack tight">
                  <strong>{route.nodeName}</strong>
                  <small className="muted">{route.reason}</small>
                </span>
              </li>
            ))}
          </ul>
          <small className="muted">
            This states where work would be eligible to run. It routes nothing and starts nothing.
          </small>
        </div>
      </div>
    </section>
  );
}

function MachinistSection({ report, onOpenFinding, onReportToMainCommand }: {
  readonly report: MachinistReport;
  readonly onOpenFinding: (target: MachinistTarget) => void;
  readonly onReportToMainCommand: () => void;
}): ReactElement {
  return (
    <section className="engine-section engine-machinist" aria-label="Machinist and Systems Supervisor" data-engine-section="machinist">
      <header className="engine-section-head">
        <h2>Machinist</h2>
        <small className="muted">Systems Supervisor · deterministic readings</small>
      </header>

      <p className="engine-machinist-summary">{report.summary}</p>

      <div className="row engine-machinist-actions">
        <button type="button" onClick={onReportToMainCommand}
          title="Put this report into the one Main Command conversation. It sends nothing to a Worker.">
          Report to Main Command
        </button>
      </div>

      {report.findings.length === 0 ? (
        <p className="muted">No finding. Every reading that exists is clean.</p>
      ) : (
        <ul className="engine-findings">
          {report.findings.map((finding: MachinistFinding) => (
            <li key={finding.id} className="engine-finding" data-severity={finding.severity} data-rule={finding.rule}>
              <span className="engine-chip" data-severity={finding.severity}>{finding.severity}</span>
              <div className="stack tight">
                <strong>{finding.title}</strong>
                <small><em>Impact:</em> {finding.impact}</small>
                <small><em>Recommended:</em> {finding.recommendedAction}</small>
                <ul className="engine-evidence">
                  {finding.evidence.map((item, index) => (
                    <li key={`${finding.id}:${index}`}>
                      <span className="muted">{item.label}:</span> {item.value}
                      {item.observedAt !== null && <span className="muted"> · {timeLabel(item.observedAt)}</span>}
                    </li>
                  ))}
                </ul>
                {finding.target !== null && (
                  <button type="button" className="link" onClick={() => onOpenFinding(finding.target!)}>
                    Open {finding.target.label}
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      <details className="engine-boundaries">
        <summary>What the Machinist will not do</summary>
        <ul>{report.boundaries.map((line) => <li key={line}>{line}</li>)}</ul>
      </details>
    </section>
  );
}

export function EngineRoom({
  model, report, onOpenMachine, onOpenAiLane, onOpenSystem, onOpenWork, onReportToMainCommand,
}: EngineRoomProps): ReactElement {
  const captain = model.captain;
  const openFinding = (target: MachinistTarget) => {
    if (target.kind === 'machine') { onOpenMachine(target.ref); return; }
    if (target.kind === 'ai') { onOpenAiLane(target.ref); return; }
    if (target.kind === 'system') { onOpenSystem(target.ref as SystemConnectorType); return; }
    onOpenWork();
  };

  return (
    <main className="work engine-room" aria-label="Engine Room" data-engine-state={captain.state}>
      <section className="engine-captain" aria-label="Captain status" data-state={captain.state}>
        <div className="engine-captain-state">
          <span className="engine-state-badge" data-state={captain.state}>{captain.label}</span>
          <p className="engine-captain-summary">{report.summary}</p>
          <small className="muted">{STATE_HINTS[captain.state]}</small>
        </div>
        <dl className="engine-captain-counts">
          <div><dt>Machines online</dt><dd>{captain.machinesOnline} / {captain.machinesTotal}</dd></div>
          <div><dt>Worker places</dt><dd>{ratio(captain.seatsInUse, captain.seatsConfigured)}</dd></div>
          <div><dt>Queued</dt><dd>{captain.queuedCount}</dd></div>
          <div data-tone={captain.blockedCount > 0 ? 'attention' : undefined}><dt>Blocked</dt><dd>{captain.blockedCount}</dd></div>
          <div data-tone={captain.infrastructureIssues > 0 ? 'attention' : undefined}><dt>System issues</dt><dd>{captain.infrastructureIssues}</dd></div>
          <div data-tone={captain.aiCapacityIssues > 0 ? 'attention' : undefined}><dt>AI issues</dt><dd>{captain.aiCapacityIssues}</dd></div>
          <div><dt>Last reading</dt><dd>{timeLabel(captain.lastUpdatedAt)}</dd></div>
        </dl>
        {captain.reasons.length > 0 && (
          <ul className="engine-captain-reasons">
            {captain.reasons.map((reason) => <li key={reason}>{reason}</li>)}
          </ul>
        )}
      </section>

      <section className="engine-section engine-bank" aria-label="Connected machines" data-engine-section="machines">
        <header className="engine-section-head">
          <h2>Engine bank</h2>
          <small className="muted">
            {model.machines.length === 0
              ? 'No computer has reported'
              : `${captain.machinesOnline} of ${captain.machinesTotal} in touch · ${model.work.eligibleRoutes} can take new work`}
          </small>
        </header>
        {model.machines.length === 0 ? (
          <p className="muted">No computer has reported to Brunel. Add one in Settings &amp; Options.</p>
        ) : (
          <div className="engine-machines">
            {model.machines.map((machine) => (
              <MachineCard key={machine.nodeId} machine={machine} onOpen={onOpenMachine} />
            ))}
          </div>
        )}
      </section>

      <AiSection model={model} onOpenAiLane={onOpenAiLane} />
      <SystemsSection systems={model.infrastructure.systems} onOpenSystem={onOpenSystem} />
      <WorkSection model={model} onOpenWork={onOpenWork} />
      <MachinistSection report={report} onOpenFinding={openFinding} onReportToMainCommand={onReportToMainCommand} />
    </main>
  );
}
